import { useEffect, useRef } from "react";

/**
 * Stars — monochrome animated starfield.
 * Sits absolutely inside a parent (parent must be position: relative).
 * White dots with subtle twinkle + slow drift. Respects reduced motion.
 */
export default function Stars({
  density = 1,          // 1 = default density
  drift = 0.02,         // pixels per frame vertical drift
  twinkle = true,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let stars = [];
    let raf = 0;
    let running = true;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.scale(dpr, dpr);
      const target = Math.floor((w * h) / 5200) * density;
      stars = Array.from({ length: target }, () => spawn(w, h));
    };

    const spawn = (w, h) => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.4 + 0.2,
      a: Math.random() * 0.9 + 0.1,
      t: Math.random() * Math.PI * 2,
      s: Math.random() * 0.02 + 0.005, // twinkle speed
      big: Math.random() < 0.008,      // occasional bigger "hero" star
    });

    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        s.y += reduce ? 0 : drift;
        if (s.y > h + 2) {
          s.y = -2;
          s.x = Math.random() * w;
        }
        if (twinkle && !reduce) {
          s.t += s.s;
          s.a = 0.35 + Math.abs(Math.sin(s.t)) * 0.65;
        }

        // core dot
        ctx.beginPath();
        ctx.fillStyle = `rgba(255,255,255,${s.a})`;
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();

        // occasional cross-glow for hero stars
        if (s.big) {
          ctx.strokeStyle = `rgba(255,255,255,${s.a * 0.35})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(s.x - 6, s.y);
          ctx.lineTo(s.x + 6, s.y);
          ctx.moveTo(s.x, s.y - 6);
          ctx.lineTo(s.x, s.y + 6);
          ctx.stroke();
        }
      }

      if (running) raf = requestAnimationFrame(draw);
    };

    resize();
    draw();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [density, drift, twinkle]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}
